import * as Sentry from '@sentry/vue'
import type { EditorSettings } from '../types/editor'
import { DEFAULT_EDITOR_SETTINGS } from '../types/editor'
import UsersService from './users'
import { useAuthStore } from '../stores/auth'

// Service for editor settings - handles both guest and authenticated users
class EditorSettingsService {
  private static readonly GUEST_STORAGE_KEY = 'escribonauta-guest-editor-settings'
  /** Same key used by useEditorSettings, so offline reads the last saved theme. */
  private static readonly LOCAL_STORAGE_KEY = 'escribonauta-editor-settings'

  private static readLocalSettings(): EditorSettings | null {
    try {
      const stored = localStorage.getItem(this.LOCAL_STORAGE_KEY)
      if (!stored) return null
      const parsedSettings = JSON.parse(stored)
      if (!parsedSettings || typeof parsedSettings !== 'object') return null
      return { ...DEFAULT_EDITOR_SETTINGS, ...parsedSettings }
    } catch (error) {
      console.error('Error loading editor settings from localStorage:', error)
      return null
    }
  }

  private static writeLocalSettings(settings: EditorSettings) {
    try {
      localStorage.setItem(this.LOCAL_STORAGE_KEY, JSON.stringify(settings))
    } catch (error) {
      console.error('Error saving editor settings to localStorage:', error)
    }
  }

  private static isGuestUser(): boolean {
    const authStore = useAuthStore()
    return authStore.userType === 'guest'
  }

  private static getCurrentUserId(): string {
    const authStore = useAuthStore()
    const user = authStore.currentUser as any

    if (!user || !user.userId) {
      throw new Error('User not authenticated or userId not available')
    }

    return user.userId
  }

  private static captureSettingsError(
    error: unknown,
    operation: 'save' | 'patch' | 'migrate',
    extra: Record<string, unknown> = {},
  ) {
    Sentry.withScope((scope) => {
      scope.setTag('feature', 'editor_settings')
      scope.setTag('layer', 'service')
      scope.setTag('operation', operation)
      scope.setTag('userType', this.isGuestUser() ? 'guest' : 'registered')
      Object.entries(extra).forEach(([key, value]) => {
        scope.setExtra(key, value)
      })
      Sentry.captureException(error)
    })
  }

  // Guests read localStorage. Registered users read the server and keep the same copy locally.
  static async getSettings(): Promise<{ data: EditorSettings }> {
    if (this.isGuestUser()) {
      // Guest user - use localStorage only
      try {
        const stored = localStorage.getItem(this.GUEST_STORAGE_KEY)
        if (stored) {
          const parsedSettings = JSON.parse(stored)
          // Merge with defaults to ensure all properties exist
          const settings = { ...DEFAULT_EDITOR_SETTINGS, ...parsedSettings }
          return { data: settings }
        }
      } catch (error) {
        console.error('Error loading guest settings from localStorage:', error)
      }

      // Return defaults if no stored settings or error
      return { data: DEFAULT_EDITOR_SETTINGS }
    }

    // Authenticated user - server when reachable, otherwise the local copy
    try {
      const userId = this.getCurrentUserId()
      const response = await UsersService.get(userId)

      const editorSettings = {
        ...DEFAULT_EDITOR_SETTINGS,
        ...(response.data.editorSettings || {}),
      }
      const cached = this.readLocalSettings()
      if (!cached || cached.theme !== editorSettings.theme) {
        this.writeLocalSettings(editorSettings)
      }

      return { data: editorSettings }
    } catch (error) {
      console.error('Error fetching editor settings:', error)
      const cached = this.readLocalSettings()
      if (cached) return { data: cached }
      throw error
    }
  }

  // Guests save locally. Registered users save on the server and in the local copy.
  static async saveSettings(settings: EditorSettings): Promise<{ data: EditorSettings }> {
    if (this.isGuestUser()) {
      // Guest user - use localStorage only
      try {
        localStorage.setItem(this.GUEST_STORAGE_KEY, JSON.stringify(settings))
        return { data: settings }
      } catch (error) {
        console.error('Error saving guest settings to localStorage:', error)
        this.captureSettingsError(error, 'save', {
          keys: Object.keys(settings),
        })
        throw new Error('Failed to save settings')
      }
    }

    // Authenticated user - local copy and server
    try {
      const userId = this.getCurrentUserId()
      const response = await UsersService.patchEditorSettings(userId, settings)
      const editorSettings = {
        ...DEFAULT_EDITOR_SETTINGS,
        ...(response.data.editorSettings || {}),
      }
      this.writeLocalSettings(editorSettings)

      return { data: editorSettings }
    } catch (error) {
      console.error('Error saving editor settings:', error)
      this.captureSettingsError(error, 'save', {
        keys: Object.keys(settings),
      })
      throw new Error('Failed to save settings')
    }
  }

  // Update specific editor settings (partial update)
  static async patchSettings(
    partialSettings: Partial<EditorSettings>,
  ): Promise<{ data: EditorSettings }> {
    if (this.isGuestUser()) {
      // Guest user - merge with existing localStorage settings
      try {
        const currentSettings = await this.getSettings()
        const mergedSettings = { ...currentSettings.data, ...partialSettings }

        localStorage.setItem(this.GUEST_STORAGE_KEY, JSON.stringify(mergedSettings))
        return { data: mergedSettings }
      } catch (error) {
        console.error('Error updating guest settings:', error)
        this.captureSettingsError(error, 'patch', {
          keys: Object.keys(partialSettings),
          partialSettings,
        })
        throw new Error('Failed to update settings')
      }
    }

    // Authenticated user - store locally and on the server
    try {
      const userId = this.getCurrentUserId()
      const response = await UsersService.patchEditorSettings(userId, partialSettings)
      const editorSettings = {
        ...DEFAULT_EDITOR_SETTINGS,
        ...(response.data.editorSettings || {}),
      }
      this.writeLocalSettings(editorSettings)

      return { data: editorSettings }
    } catch (error) {
      console.error('Error updating editor settings:', error)
      this.captureSettingsError(error, 'patch', {
        keys: Object.keys(partialSettings),
        partialSettings,
      })
      throw new Error('Failed to update settings')
    }
  }

  // Reset settings to default
  static async resetSettings(): Promise<{ data: EditorSettings }> {
    if (this.isGuestUser()) {
      // Guest user - reset localStorage to defaults
      try {
        localStorage.removeItem(this.GUEST_STORAGE_KEY)
        return { data: DEFAULT_EDITOR_SETTINGS }
      } catch (error) {
        console.error('Error resetting guest settings:', error)
        throw new Error('Failed to reset settings')
      }
    }

    // Authenticated user - reset backend settings and the local copy
    try {
      const userId = this.getCurrentUserId()
      const response = await UsersService.patchEditorSettings(userId, DEFAULT_EDITOR_SETTINGS)
      const editorSettings = {
        ...DEFAULT_EDITOR_SETTINGS,
        ...(response.data.editorSettings || {}),
      }
      this.writeLocalSettings(editorSettings)

      return { data: editorSettings }
    } catch (error) {
      console.error('Error resetting editor settings:', error)
      throw new Error('Failed to reset settings')
    }
  }

  // Migrate guest settings to backend (called when user authenticates)
  static async migrateGuestSettingsToBackend(): Promise<void> {
    let settingsSnapshot: Partial<EditorSettings> | undefined

    try {
      // Get guest settings from localStorage
      const guestSettings = localStorage.getItem(this.GUEST_STORAGE_KEY)

      if (!guestSettings) return

      const parsedGuestSettings = JSON.parse(guestSettings)

      // Merge with defaults to ensure all properties exist
      const settingsToMigrate = { ...DEFAULT_EDITOR_SETTINGS, ...parsedGuestSettings }
      settingsSnapshot = {
        fontSize: settingsToMigrate.fontSize,
        fontFamily: settingsToMigrate.fontFamily,
        pageWidth: settingsToMigrate.pageWidth,
        lineHeight: settingsToMigrate.lineHeight,
        indentSize: settingsToMigrate.indentSize,
        paragraphSpacing: settingsToMigrate.paragraphSpacing,
      }

      // Save to backend
      const userId = this.getCurrentUserId()
      await UsersService.patchEditorSettings(userId, settingsToMigrate)

      // Clean up guest settings from localStorage
      localStorage.removeItem(this.GUEST_STORAGE_KEY)
    } catch (error) {
      console.error('Error migrating guest settings to backend:', error)
      this.captureSettingsError(error, 'migrate', {
        guestVisualSettings: settingsSnapshot,
      })
      // Don't throw error - migration is not critical
    }
  }
}

export default EditorSettingsService
